import { PropsWithChildren, createContext, useContext, useState } from 'react'
import toast, { type ToastOptions } from 'react-hot-toast'
import { PipType } from '../components/Pip'
import { ImageModal, ImageModalProps } from '../components/ImageModal'
import { getMessageFromError } from '../helpers/errors'
import { ErrorType } from '../pages/meeting-view/components/Video/ErrorComponent'

const UIContext = createContext<UIContextType | undefined>(undefined)
UIContext.displayName = 'UIContext'

const UIProvider = ({ children }: PropsWithChildren) => {
    const [error, setError] = useState<ErrorType | null>(null)
    const [pip, setPip] = useState<PipType | null>(null)
    const [imageModal, setImageModal] = useState<ImageModalProps | null>(null)
    const [isHelpActive, setIsHelpActive] = useState(false)
    const showToast = toast

    const openPip = (pip: PipType) => {
        const { mute = true, onClose, onMute, onUnmute, ...rest } = pip
        if (mute) onMute?.()

        setPip({
            ...rest,
            mute,
            onMute: () => {
                onMute?.()
                setPip(old => ({ ...old, mute: true }))
            },
            onUnmute: () => {
                onUnmute?.()
                setPip(old => ({ ...old, mute: false }))
            },
            onClose: () => {
                if (mute) onUnmute?.()
                onClose?.()
                setPip(null)
            },
        })
    }

    const openImageModal = (imageModalProps: ImageModalProps) => {
        const { onClose, ...rest } = imageModalProps

        setImageModal({
            ...rest,
            onClose: () => {
                onClose?.()
                setImageModal(null)
            },
        })
    }

    const showError = (error: unknown, options?: ToastOptions) => {
        const message = getMessageFromError(error)
        return toast.error(message, options)
    }

    const closePip = () => pip?.onClose?.()
    const closeImageModal = () => imageModal?.onClose?.()

    const values = {
        showToast,
        showError,
        error,
        setError,
        pip,
        openPip,
        closePip,
        imageModal,
        openImageModal,
        closeImageModal,
        isHelpActive,
        setIsHelpActive,
    }

    return (
        <UIContext.Provider value={values}>
            {children}
            <ImageModal />
        </UIContext.Provider>
    )
}

const useUI = () => {
    const context = useContext(UIContext)
    if (context === undefined) {
        throw new Error('useUI must be used within a UIProvider')
    }
    return context
}

export { UIProvider, useUI }

export interface UIContextType {
    showToast: typeof toast
    showError: (error: unknown, options?: ToastOptions) => string
    error: ErrorType | null
    setError: (error: ErrorType | null) => void
    pip: PipType | null
    openPip: (props: PipType) => void
    closePip: () => void
    imageModal: ImageModalProps | null
    openImageModal: (props: ImageModalProps) => void
    closeImageModal: () => void
    isHelpActive: boolean
    setIsHelpActive: (isHelpActive: boolean) => void
}
